"use client";

import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type PaginationState,
} from "@tanstack/react-table";
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import type { DataTableColumnPhone, DataTableProps } from "../types";
import styles from "./DataTable.module.css";

/** The class for each place of a cell in a stacked row. */
const PHONE_CELL_CLASS: Record<DataTableColumnPhone, string> = {
  title: styles.cellTitle,
  end: styles.cellEnd,
  hide: styles.cellHide,
};

/**
 * Classes for a body cell: the base class, the column class from
 * `meta.className`, and in a stacked table the place from `meta.phone`.
 */
function cellClassName(
  base: string,
  meta: { className?: string; phone?: DataTableColumnPhone } | undefined,
  stack: boolean,
): string {
  return [base, meta?.className, stack && meta?.phone ? PHONE_CELL_CLASS[meta.phone] : undefined]
    .filter(Boolean)
    .join(" ");
}

/**
 * A generic, sortable data table built on TanStack Table.
 *
 * Styled with CSS Modules and CSS variables for design-system consistency.
 * Supports sorting, pagination, row click, loading skeletons, and empty states.
 */
export function DataTable<TData>({
  columns,
  data,
  onRowClick,
  getRowClassName,
  isLoading = false,
  emptyMessage = "No data",
  skeletonRowCount = 5,
  pageSize,
  initialSorting,
  paginationResetKey,
  fitContent = false,
  fixedLayout = false,
  phoneLayout = "scroll",
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? []);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? (data.length || 100),
  });

  // Keep pageSize in sync with prop
  React.useEffect(() => {
    if (pageSize !== undefined) {
      setPagination((prev) => ({ ...prev, pageSize }));
    }
  }, [pageSize]);

  const enablePagination = pageSize !== undefined && data.length > pageSize;

  React.useEffect(() => {
    if (!enablePagination) return;

    setPagination((prev) => {
      const maxPageIndex = Math.max(Math.ceil(data.length / prev.pageSize) - 1, 0);
      if (prev.pageIndex <= maxPageIndex) {
        return prev;
      }
      return { ...prev, pageIndex: maxPageIndex };
    });
  }, [data.length, enablePagination, pagination.pageSize]);

  React.useEffect(() => {
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [paginationResetKey]);

  const handleSortingChange = React.useCallback((updater: React.SetStateAction<SortingState>) => {
    setSorting(updater);
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(enablePagination ? { pagination } : {}),
    },
    onSortingChange: handleSortingChange,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    autoResetPageIndex: false,
  });

  const stack = phoneLayout === "stack";

  return (
    <div
      className={[styles.wrapper, fitContent && styles.wrapperFitContent, stack && styles.stack]
        .filter(Boolean)
        .join(" ")}
    >
      <table
        className={[styles.table, fitContent && styles.tableFitContent, fixedLayout && styles.tableFixedLayout].filter(Boolean).join(" ")}
      >
        <thead className={styles.thead}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                const thClasses = [
                  styles.th,
                  canSort ? styles.sortable : "",
                  header.column.columnDef.meta?.className,
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <th
                    key={header.id}
                    className={thClasses}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  >
                    <div className={styles.thContent}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <SortIcon direction={sorted} />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading ? (
            <SkeletonRows
              columnCount={columns.length}
              rowCount={skeletonRowCount}
              cellClassNames={table
                .getVisibleLeafColumns()
                .map((column) => cellClassName(styles.skeletonCell, column.columnDef.meta, stack))}
            />
          ) : table.getRowModel().rows.length === 0 ? (
            <tr className={styles.emptyRow}>
              <td className={styles.emptyCell} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const rowClasses = [
                styles.tr,
                onRowClick ? styles.trClickable : "",
                getRowClassName?.(row.original) ?? "",
              ].join(" ");

              return (
                <tr
                  key={row.id}
                  className={rowClasses}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cellClassName(styles.td, cell.column.columnDef.meta, stack)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Pagination controls */}
      {enablePagination && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <div className={styles.pageButtons}>
            <button
              className={styles.pageButton}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <ChevronLeft className={styles.pageIcon} />
              Prev
            </button>
            <button
              className={styles.pageButton}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              Next
              <ChevronRight className={styles.pageIcon} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Sort direction indicator icon */
function SortIcon({ direction }: { direction: false | "asc" | "desc" }) {
  if (direction === "asc") {
    return <ArrowUp className={`${styles.sortIcon} ${styles.sortIconActive}`} />;
  }
  if (direction === "desc") {
    return <ArrowDown className={`${styles.sortIcon} ${styles.sortIconActive}`} />;
  }
  return <ArrowUpDown className={styles.sortIcon} />;
}

/** Skeleton loading rows */
function SkeletonRows({
  columnCount,
  rowCount,
  cellClassNames,
}: {
  columnCount: number;
  rowCount: number;
  /** Class of each cell, by column, so a skeleton row has the place of a row. */
  cellClassNames: string[];
}) {
  const widths = ["60%", "68%", "76%", "84%"] as const;

  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIdx) => (
        <tr key={rowIdx} className={styles.tr}>
          {Array.from({ length: columnCount }).map((_, colIdx) => (
            <td key={colIdx} className={cellClassNames[colIdx] ?? styles.skeletonCell}>
              <div
                className={styles.skeleton}
                style={{ width: widths[(rowIdx + colIdx) % widths.length] }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
